import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TmBaseComponent } from '@treasury/presentation';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import type { FormLayoutResponsiveStep } from '@vaadin/form-layout';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ReportFiltersVm } from '../data/report-filters.vm';

import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-currency-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';
// eslint-disable-next-line no-duplicate-imports
import '@vaadin/form-layout';

const defaultDateRange: DateRange = {
    rangeType: DateRangeType.Today,
};

const tagName = 'edi-report-filters';
@customElement(tagName)
export class EdiReportFilters extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public range = false;

    @state()
    private filters?: ReportFiltersVm;

    @state()
    private dateRange: DateRange = defaultDateRange;

    @state()
    private isValid = false;

    private responsiveSteps: FormLayoutResponsiveStep[] = [
        { minWidth: 0, columns: 1 },
        { minWidth: '768px', columns: 2 },
        { minWidth: '1024px', columns: 3 },
    ];

    public render() {
        if (!this.filters) {
            return nothing;
        }
        return html`<vaadin-form-layout .responsiveSteps=${this.responsiveSteps}
            ><tm-radio-group
                label="Status"
                .initial=${this.filters.initialStatus}
                .options=${this.filters.statusItems}
                @value-changed=${(e: CustomEvent) => {
                    if (!this.filters) {
                        return;
                    }

                    this.filters.postedTransactions = e.detail.value;
                    this.dispatchFiltersChanged();
                }}
            ></tm-radio-group>
            <tm-select
                label="Account"
                required
                .items=${this.filters.allAccountItems}
                @value-changed=${(e: TmValueChanged<CompanyAccount>) => {
                    if (!e.detail.value || !this.filters) {
                        return;
                    }
                    this.filters.account = e.detail.value;
                    this.dispatchFiltersChanged();
                }}
            ></tm-select>
            <tm-date-range
                label="Date"
                required
                .initial=${this.filters.initialDateRange}
                .maxDate=${new Date()}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { isValid, value } = e.detail;
                    if (value && this.filters) {
                        this.isValid = isValid;
                        this.filters.dateType = value.rangeType;
                        this.filters.fromDate = value.startDate ? format(value.startDate, 'P') : '';
                        this.filters.toDate = value.endDate ? format(value.endDate, 'P') : '';
                        this.filters.specificDate =
                            value.startDate && value.rangeType === DateRangeType.SpecificDate
                                ? format(value.startDate, 'P')
                                : '';
                    }
                }}
            ></tm-date-range>
            <!-- custom currency range options/values here need to be wired up -->
            <tm-currency-range label="Amount Range"></tm-currency-range>
            <tm-radio-group
                label="Amount Type"
                .options=${this.filters.amountTypeItems}
            ></tm-radio-group
        ></vaadin-form-layout>`;
    }

    private dispatchFiltersChanged() {
        this.dispatchEvent(
            new CustomEvent('filters-changed', {
                detail: {
                    filters: this.filters,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: EdiReportFilters;
    }
}
